import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';

import { i18n } from '~/lib/i18n';

// ── Types ────────────────────────────────────────────────────────────────────

export type ReviewRow = {
  name: string;
  email: string;
  phone: string;
  nameError: string | null;
  emailError: string | null;
  phoneError: string | null;
  parsedPhoneNumber: string | null;
  parsedPhoneCountryCode: string | null;
};

export type ImportResult = { created: number; conflict: number; error: number };

// ── Phone parser ─────────────────────────────────────────────────────────────

export function parsePhone(raw: string): {
  parsedPhoneNumber: string | null;
  parsedPhoneCountryCode: string | null;
  phoneError: string | null;
} {
  try {
    const trimmed = raw.trim();
    if (!isValidPhoneNumber(trimmed)) throw new Error();
    const parsed = parsePhoneNumberWithError(trimmed);
    return {
      parsedPhoneNumber: parsed.nationalNumber,
      parsedPhoneCountryCode: `+${parsed.countryCallingCode}`,
      phoneError: null
    };
  } catch {
    return {
      parsedPhoneNumber: null,
      parsedPhoneCountryCode: null,
      phoneError: i18n.t('applications.importDialog.errors.phoneInvalid')
    };
  }
}

// ── Row validator ────────────────────────────────────────────────────────────

export function validateRow(row: ReviewRow): ReviewRow {
  const nameError =
    row.name.trim().length === 0 ? i18n.t('applications.importDialog.errors.nameRequired') : null;
  const emailError = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim())
    ? i18n.t('applications.importDialog.errors.emailInvalid')
    : null;

  const { parsedPhoneNumber, parsedPhoneCountryCode, phoneError } = parsePhone(row.phone);

  return { ...row, nameError, emailError, phoneError, parsedPhoneNumber, parsedPhoneCountryCode };
}

export function markDuplicates(rows: ReviewRow[]): ReviewRow[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = row.email.trim().toLowerCase();
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return rows.map((row) => {
    const key = row.email.trim().toLowerCase();
    if (!row.emailError && key && (counts.get(key) ?? 0) > 1) {
      return { ...row, emailError: i18n.t('applications.importDialog.errors.emailDuplicate') };
    }
    return row;
  });
}

export function validateAll(rows: ReviewRow[]): ReviewRow[] {
  return markDuplicates(rows.map(validateRow));
}
